import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import NetworkVisual from '../components/visuals/NetworkVisual';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const RefundPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      title: "Introduction",
      content: "At PR Boost, we strive to provide the highest quality service to our clients. We are committed to your satisfaction, and we want to ensure you have a positive experience with our services. This Refund Policy outlines the circumstances under which we offer refunds for our services."
    },
    {
      title: "Eligibility for Refunds",
      content: "Refunds are granted under specific conditions as detailed below. To be eligible for a refund, you must meet the criteria outlined in this policy.",
      subsections: [
        {
          subtitle: "1. Service Non-Delivery",
          text: "If we are unable to publish your news story on the agreed-upon news outlets included in your chosen publishing package, you will be entitled to a full refund."
        },
        {
          subtitle: "2. Service Cancellation",
          text: "If you wish to cancel your order, you must do so within 24 hours of purchase. Cancellations made within this timeframe are eligible for a full refund. After 24 hours, cancellations are not eligible for a refund."
        },
        {
          subtitle: "3. Unsatisfactory Service",
          text: "If you are not satisfied with the quality of the written article or the placement of your news story, please contact us within 7 days of receiving your report. We will work with you to address your concerns and may offer a partial refund or additional services to rectify the situation."
        }
      ]
    },
    {
      title: "Non-Refundable Services",
      content: "Certain services are non-refundable, including but not limited to:",
      list: [
        { title: "Completed Services", text: "Any completed services where the article has been published as per the agreed package." },
        { title: "Customized Fulfillment", text: "Customized services that have been fulfilled as per the client's specific requirements." },
        { title: "Add-ons", text: "Any additional services or add-ons that have already been completed." }
      ]
    },
    {
      title: "Refund Process",
      content: "To request a refund, please follow these steps:",
      list: [
        { title: "Contact Us", text: "Email us at support@prboost.io with your order number and a detailed description of your issue." },
        { title: "Review", text: "Our support team will review your request and respond within 3-5 business days." },
        { title: "Resolution", text: "If your refund request is approved, we will process your refund within 7 business days to the original payment method." }
      ]
    },
    {
      title: "Changes to This Refund Policy",
      content: "PR Boost reserves the right to update or change this Refund Policy at any time. Any changes will be posted on this page, and your continued use of our services constitutes acceptance of the updated Refund Policy."
    }
  ];

  return (
    <div className="bg-[#0B0F19] min-h-screen">
      {/* Header Section */}
      <section className="relative pt-40 pb-24 overflow-hidden border-b border-white/5">
        <NetworkVisual />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold uppercase tracking-wider mb-6 border border-blue-500/20"
            >
              <SafeIcon icon={FiIcons.FiRotateCcw} /> Billing Protection
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-7xl font-extrabold text-white mb-6 tracking-tight"
            >
              Refund <span className="text-blue-500 text-gradient">Policy</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-slate-400 font-medium"
            >
              Last updated: {new Date().toLocaleDateString()}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-24 relative">
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="glass-card p-8 md:p-16 rounded-[2.5rem] border-white/5 bg-[#12151e]/80 backdrop-blur-xl shadow-2xl">
            <div className="space-y-16">
              {sections.map((section, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className="group"
                >
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-4">
                    <span className="text-blue-500/80 text-4xl font-mono group-hover:text-blue-400 transition-colors">
                      {(idx + 1).toString().padStart(2, '0')}
                    </span>
                    {section.title}
                  </h2>
                  
                  {section.content && (
                    <p className="text-slate-400 text-lg leading-relaxed mb-6">
                      {section.content}
                    </p>
                  )}

                  {section.subsections && (
                    <div className="grid gap-6 mt-8">
                      {section.subsections.map((sub, sIdx) => (
                        <div key={sIdx} className="bg-white/5 p-6 rounded-2xl border border-white/5">
                          <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                            {sub.subtitle}
                          </h4>
                          <p className="text-slate-400 leading-relaxed">
                            {sub.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {section.list && (
                    <div className="space-y-6 mt-6">
                      {section.list.map((item, lIdx) => (
                        <div key={lIdx} className="flex gap-4">
                          <div className="mt-1.5 text-blue-500">
                            <SafeIcon icon={FiIcons.FiCheckCircle} />
                          </div>
                          <div>
                            <span className="text-white font-bold">{item.title}: </span>
                            <span className="text-slate-400 leading-relaxed">{item.text}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}

              {/* Contact Us Section */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="pt-12 border-t border-white/5"
              >
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">Contact Information</h2>
                <div className="bg-blue-500/5 border border-blue-500/20 p-8 rounded-3xl flex flex-col md:flex-row items-center justify-between gap-8">
                  <div className="flex items-center gap-6">
                    <div className="w-16 h-16 rounded-2xl bg-blue-500/20 flex items-center justify-center text-blue-400">
                      <SafeIcon icon={FiIcons.FiMail} className="text-3xl" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-xl">PR Boost</h4>
                      <p className="text-blue-400 font-medium">support@prboost.io</p>
                    </div>
                  </div>
                  <div className="text-center md:text-right">
                    <p className="text-slate-400 text-sm max-w-[200px]">
                      Reach out to our billing support team for any refund requests.
                    </p>
                  </div>
                </div>
              </motion.div>

              <p className="text-center text-slate-500 text-sm italic pt-12">
                By using our services, you acknowledge that you have read, understood, and agree to the terms of this Refund Policy.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RefundPolicy;